
// src/pages/StaffDashboard.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styles from '../styles/admin.module.css';

const AdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Lecturer Profile State
  const [lecturerName, setLecturerName] = useState('');
  const [lecturerBio, setLecturerBio] = useState('');
  const [lecturerImage, setLecturerImage] = useState<File | null>(null);
  const [lecturers, setLecturers] = useState<Array<{name: string, bio: string, imageUrl: string}>>([]);
  
  // Student Stats State
  const [phdCount, setPhdCount] = useState('');
  const [mastersCount, setMastersCount] = useState('');
  const [undergradCount, setUndergradCount] = useState('');
  const [studentStats, setStudentStats] = useState({
    phd: 0,
    masters: 0,
    undergrad: 0
  });
  
  // News State
  const [newsTitle, setNewsTitle] = useState('');
  const [newsContent, setNewsContent] = useState('');
  const [news, setNews] = useState<Array<{title: string, content: string}>>([]);
  
  // Events State
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventStatus, setEventStatus] = useState('upcoming');
  const [events, setEvents] = useState<Array<{
    title: string, 
    description: string, 
    date: string, 
    status: string
  }>>([]);

  // Handle lecturer profile creation
  const handleCreateLecturer = () => {
    if (lecturerName && lecturerBio) {
      // In a real app, you would upload the image to a server and get a URL
      const imageUrl = lecturerImage ? URL.createObjectURL(lecturerImage) : '';
      
      setLecturers([...lecturers, {
        name: lecturerName,
        bio: lecturerBio,
        imageUrl
      }]);
      
      setLecturerName('');
      setLecturerBio('');
      setLecturerImage(null);
      alert('Lecturer profile created successfully!');
    }
  };

  // Handle student stats update
  const handleUpdateStats = () => {
    setStudentStats({
      phd: parseInt(phdCount) || 0,
      masters: parseInt(mastersCount) || 0,
      undergrad: parseInt(undergradCount) || 0
    });
    alert('Student statistics updated!');
  };

  // Handle news posting
  const handlePostNews = () => {
    if (newsTitle && newsContent) {
      setNews([...news, { title: newsTitle, content: newsContent }]);
      setNewsTitle('');
      setNewsContent('');
      alert('News posted successfully!');
    }
  };

  // Handle event creation
  const handleCreateEvent = () => {
    if (eventTitle && eventDescription && eventDate) {
      setEvents([...events, {
        title: eventTitle,
        description: eventDescription,
        date: eventDate,
        status: eventStatus
      }]);
      
      setEventTitle('');
      setEventDescription('');
      setEventDate('');
      setEventStatus('upcoming');
      alert('Event created successfully!');
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className={styles.dashboardContainer}>
      {/* Sidebar */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <h2>Philosophy Department</h2>
          <p>Staff Dashboard</p>
        </div>
        
        <nav className={styles.sidebarNav}>
          <button 
            className={`${styles.navItem} ${activeTab === 'dashboard' ? styles.active : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <i className="fas fa-tachometer-alt"></i>
            Dashboard
          </button>
          
          <button 
            className={`${styles.navItem} ${activeTab === 'lecturers' ? styles.active : ''}`}
            onClick={() => setActiveTab('lecturers')}
          >
            <i className="fas fa-user-tie"></i>
            Lecturer Profiles
          </button>
          
          <button 
            className={`${styles.navItem} ${activeTab === 'students' ? styles.active : ''}`}
            onClick={() => setActiveTab('students')}
          >
            <i className="fas fa-user-graduate"></i>
            Student Stats
          </button>
          
          <button 
            className={`${styles.navItem} ${activeTab === 'news' ? styles.active : ''}`}
            onClick={() => setActiveTab('news')}
          >
            <i className="fas fa-newspaper"></i>
            Post News
          </button>
          
          <button 
            className={`${styles.navItem} ${activeTab === 'events' ? styles.active : ''}`}
            onClick={() => setActiveTab('events')}
          >
            <i className="fas fa-calendar-alt"></i>
            Manage Events
          </button>
        </nav>
        
        <div className={styles.sidebarFooter}>
          <Link to="/" className={styles.logoutBtn}>
            <i className="fas fa-sign-out-alt"></i>
            Back to Main Site
          </Link>
        </div>
      </div>
      
      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className={styles.dashboardTab}>
            <h1 className={styles.pageTitle}>Dashboard Overview</h1>
            
            <div className={styles.statsGrid}>
              <div className={`${styles.statCard} ${styles.card1}`}>
                <h3>Lecturers</h3>
                <p>{lecturers.length}</p>
                <i className="fas fa-user-tie"></i>
              </div>
              
              <div className={`${styles.statCard} ${styles.card2}`}>
                <h3>PhD Students</h3>
                <p>{studentStats.phd}</p>
                <i className="fas fa-graduation-cap"></i>
              </div>
              
              <div className={`${styles.statCard} ${styles.card3}`}>
                <h3>Masters Students</h3>
                <p>{studentStats.masters}</p>
                <i className="fas fa-user-graduate"></i>
              </div>
              
              <div className={`${styles.statCard} ${styles.card4}`}>
                <h3>Undergrad Students</h3>
                <p>{studentStats.undergrad}</p>
                <i className="fas fa-users"></i>
              </div>
            </div>
            
            <div className={styles.activitySection}>
              <h2>Recent Activity</h2>
              <div className={styles.activityList}>
                {lecturers.slice(-3).map((lecturer, index) => (
                  <div key={index} className={styles.activityItem}>
                    <div className={styles.activityIcon}>
                      <i className="fas fa-user-tie"></i>
                    </div>
                    <div className={styles.activityText}>
                      <p>Created profile: <strong>{lecturer.name}</strong></p>
                      <small>Just now</small>
                    </div>
                  </div>
                ))}
                
                {news.slice(-3).map((item, index) => (
                  <div key={index} className={styles.activityItem}>
                    <div className={styles.activityIcon}>
                      <i className="fas fa-bullhorn"></i>
                    </div>
                    <div className={styles.activityText}>
                      <p>Posted news: <strong>{item.title}</strong></p>
                      <small>2 hours ago</small>
                    </div>
                  </div>
                ))}
                
                {events.slice(-3).map((event, index) => (
                  <div key={index} className={styles.activityItem}>
                    <div className={styles.activityIcon}>
                      <i className="fas fa-calendar-alt"></i>
                    </div>
                    <div className={styles.activityText}>
                      <p>Created event: <strong>{event.title}</strong></p>
                      <small>{formatDate(event.date)}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Lecturer Profiles Tab */}
        {activeTab === 'lecturers' && (
          <div className={styles.lecturersTab}>
            <h1 className={styles.pageTitle}>Create Lecturer Profile</h1>
            
            <div className={styles.formCard}>
              <div className={styles.formGroup}>
                <label>Lecturer Name</label>
                <input 
                  type="text" 
                  value={lecturerName}
                  onChange={(e) => setLecturerName(e.target.value)}
                  placeholder="Enter lecturer's full name"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label>Profile Image (Optional)</label>
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => setLecturerImage(e.target.files?.[0] || null)}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label>Biography</label>
                <textarea 
                  value={lecturerBio}
                  onChange={(e) => setLecturerBio(e.target.value)}
                  placeholder="Enter lecturer's biography..."
                  rows={8}
                ></textarea>
              </div>
              
              <div className={styles.formActions}>
                <button className={styles.primaryBtn} onClick={handleCreateLecturer}>
                  Create Profile
                </button>
              </div>
            </div>
            
            <div className={styles.lecturersList}>
              <h2>Department Lecturers ({lecturers.length})</h2>
              
              {lecturers.length > 0 ? (
                <div className={styles.lecturersGrid}>
                  {lecturers.map((lecturer, index) => (
                    <div key={index} className={styles.lecturerCard}>
                      {lecturer.imageUrl ? (
                        <img 
                          src={lecturer.imageUrl} 
                          alt={lecturer.name} 
                          className={styles.lecturerImage}
                        />
                      ) : (
                        <div className={styles.lecturerPlaceholder}>
                          <i className="fas fa-user-tie"></i>
                        </div>
                      )}
                      <div className={styles.lecturerInfo}>
                        <h3>{lecturer.name}</h3>
                        <p>{lecturer.bio.substring(0, 150)}...</p>
                        <div className={styles.lecturerActions}>
                          <button className={styles.actionBtn}>
                            <i className="fas fa-edit"></i> Edit
                          </button>
                          <button className={styles.actionBtn}>
                            <i className="fas fa-trash"></i> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <i className="fas fa-user-tie"></i>
                  <p>No lecturer profiles created yet</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Student Stats Tab */}
        {activeTab === 'students' && (
          <div className={styles.studentsTab}>
            <h1 className={styles.pageTitle}>Update Student Statistics</h1>
            
            <div className={styles.formCard}>
              <div className={styles.statsGrid}>
                <div className={styles.statInput}>
                  <label>PhD Students</label>
                  <input 
                    type="number" 
                    value={phdCount}
                    onChange={(e) => setPhdCount(e.target.value)}
                    placeholder="Enter number"
                    min="0"
                  />
                </div>
                
                <div className={styles.statInput}>
                  <label>Masters Students</label>
                  <input 
                    type="number" 
                    value={mastersCount}
                    onChange={(e) => setMastersCount(e.target.value)}
                    placeholder="Enter number"
                    min="0"
                  />
                </div>
                
                <div className={styles.statInput}>
                  <label>Undergraduate Students</label>
                  <input 
                    type="number" 
                    value={undergradCount}
                    onChange={(e) => setUndergradCount(e.target.value)}
                    placeholder="Enter number"
                    min="0"
                  />
                </div>
              </div>
              
              <div className={styles.formActions}>
                <button className={styles.primaryBtn} onClick={handleUpdateStats}>
                  Update Statistics
                </button>
              </div>
            </div>
            
            <div className={styles.statsDisplay}>
              <h2>Current Student Statistics</h2>
              <div className={styles.statsCards}>
                <div className={styles.statsCard}>
                  <div className={styles.statsHeader}>
                    <i className="fas fa-graduation-cap"></i>
                    <h3>PhD Students</h3>
                  </div>
                  <div className={styles.statsValue}>{studentStats.phd}</div>
                </div>
                
                <div className={styles.statsCard}>
                  <div className={styles.statsHeader}>
                    <i className="fas fa-user-graduate"></i>
                    <h3>Masters Students</h3>
                  </div>
                  <div className={styles.statsValue}>{studentStats.masters}</div>
                </div>
                
                <div className={styles.statsCard}>
                  <div className={styles.statsHeader}>
                    <i className="fas fa-users"></i>
                    <h3>Undergraduate Students</h3>
                  </div>
                  <div className={styles.statsValue}>{studentStats.undergrad}</div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* News Tab */}
        {activeTab === 'news' && (
          <div className={styles.newsTab}>
            <h1 className={styles.pageTitle}>Post Department News</h1>
            
            <div className={styles.formCard}>
              <div className={styles.formGroup}>
                <label>News Title</label>
                <input 
                  type="text" 
                  value={newsTitle}
                  onChange={(e) => setNewsTitle(e.target.value)}
                  placeholder="Enter news headline"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label>News Content</label>
                <textarea 
                  value={newsContent}
                  onChange={(e) => setNewsContent(e.target.value)}
                  placeholder="Enter news content..."
                  rows={8}
                ></textarea>
              </div>
              
              <div className={styles.formActions}>
                <button className={styles.primaryBtn} onClick={handlePostNews}>
                  Publish News
                </button>
              </div>
            </div>
            
            <div className={styles.newsList}>
              <h2>Recent News ({news.length})</h2>
              
              {news.length > 0 ? (
                <div className={styles.newsGrid}>
                  {news.map((item, index) => (
                    <div key={index} className={styles.newsCard}>
                      <h3>{item.title}</h3>
                      <p>{item.content.substring(0, 150)}...</p>
                      <div className={styles.newsMeta}>
                        <span><i className="fas fa-calendar"></i> Today</span>
                      </div>
                      <div className={styles.newsActions}>
                        <button className={styles.actionBtn}>
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        <button className={styles.actionBtn}>
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <i className="fas fa-newspaper"></i>
                  <p>No news posted yet</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className={styles.eventsTab}>
            <h1 className={styles.pageTitle}>Manage Department Events</h1>
            
            <div className={styles.formCard}>
              <div className={styles.formGroup}>
                <label>Event Title</label>
                <input 
                  type="text" 
                  value={eventTitle}
                  onChange={(e) => setEventTitle(e.target.value)}
                  placeholder="Enter event title"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label>Event Description</label>
                <textarea 
                  value={eventDescription}
                  onChange={(e) => setEventDescription(e.target.value)}
                  placeholder="Enter event description..."
                  rows={6}
                ></textarea>
              </div>
              
              <div className={styles.formGroupRow}>
                <div className={styles.formGroup}>
                  <label>Event Date & Time</label>
                  <input 
                    type="datetime-local" 
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label>Event Status</label>
                  <select
                    value={eventStatus}
                    onChange={(e) => setEventStatus(e.target.value)}
                  >
                    <option value="upcoming">Upcoming</option>
                    <option value="past">Past Event</option>
                  </select>
                </div>
              </div>
              
              <div className={styles.formActions}>
                <button className={styles.primaryBtn} onClick={handleCreateEvent}>
                  Create Event
                </button>
              </div>
            </div>
            
            <div className={styles.eventsList}>
              <div className={styles.eventsFilter}>
                <button className={`${styles.filterBtn} ${eventStatus === 'upcoming' ? styles.active : ''}`}
                  onClick={() => setEventStatus('upcoming')}>
                  Upcoming Events
                </button>
                <button className={`${styles.filterBtn} ${eventStatus === 'past' ? styles.active : ''}`}
                  onClick={() => setEventStatus('past')}>
                  Past Events
                </button>
              </div>
              
              <h2>{eventStatus === 'upcoming' ? 'Upcoming' : 'Past'} Events ({events.filter(e => e.status === eventStatus).length})</h2>
              
              {events.filter(e => e.status === eventStatus).length > 0 ? (
                <div className={styles.eventsGrid}>
                  {events
                    .filter(e => e.status === eventStatus)
                    .map((event, index) => (
                      <div key={index} className={`${styles.eventCard} ${event.status === 'past' ? styles.pastEvent : ''}`}>
                        <div className={styles.eventDate}>
                          <div className={styles.eventDay}>
                            {new Date(event.date).getDate()}
                          </div>
                          <div className={styles.eventMonth}>
                            {new Date(event.date).toLocaleString('default', { month: 'short' })}
                          </div>
                        </div>
                        <div className={styles.eventDetails}>
                          <h3>{event.title}</h3>
                          <p className={styles.eventTime}>
                            <i className="fas fa-clock"></i> {formatDate(event.date)}
                          </p>
                          <p>{event.description.substring(0, 100)}...</p>
                          <div className={styles.eventStatus}>
                            <span className={`${styles.statusBadge} ${event.status === 'upcoming' ? styles.upcoming : styles.past}`}>
                              {event.status === 'upcoming' ? 'Upcoming' : 'Past Event'}
                            </span>
                          </div>
                          <div className={styles.eventActions}>
                            <button className={styles.actionBtn}>
                              <i className="fas fa-edit"></i> Edit
                            </button>
                            <button className={styles.actionBtn}>
                              <i className="fas fa-trash"></i> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <i className="fas fa-calendar-alt"></i>
                  <p>No {eventStatus === 'upcoming' ? 'upcoming' : 'past'} events scheduled</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;

